import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Chip, FormControlLabel, Stack, Switch, Typography,
} from '@mui/material';
import { JsonForm } from '../components/JsonForm';
import { singleGameLeaderboardHint } from '../schema';
import { useLeaderboardContext } from './EditLeaderboardPage';

interface BpRow {
  village: string;
  points: number;
  description: string;
  kind: 'bonus' | 'penalty';
  scope: string; // division name, or '' for game-level
}

// Flatten the game's applied bonuses/penalties (game-level + per-division)
// out of its palio_games_status entry.
function collectBonusesPenalties(status: any): BpRow[] {
  if (!status) return [];
  const rows: BpRow[] = [];
  const push = (list: any, kind: BpRow['kind'], scope: string) => {
    (Array.isArray(list) ? list : []).forEach((e: any) => rows.push({
      village: e?.village ?? '?',
      points: Math.abs(Number(e?.points) || 0),
      description: e?.description ?? '',
      kind,
      scope,
    }));
  };
  push(status.applied_bonuses, 'bonus', '');
  push(status.applied_penalties, 'penalty', '');
  (Array.isArray(status.divisions) ? status.divisions : []).forEach((d: any) => {
    push(d?.applied_bonuses, 'bonus', d?.name ?? '');
    push(d?.applied_penalties, 'penalty', d?.name ?? '');
  });
  return rows;
}

const LeaderboardGameView: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const { content, setContent, villages, gameStatusById } = useLeaderboardContext();

  if (!gameId) return null;

  const game = content.game_leaderboards?.[gameId];
  if (game == null) {
    return (
      <Alert severity="warning">
        Classifica non trovata per questo gioco.
      </Alert>
    );
  }

  const setGame = (nv: typeof game) =>
    setContent((prev) => ({
      ...prev,
      game_leaderboards: { ...(prev.game_leaderboards ?? {}), [gameId]: nv },
    }));

  const bonusesPenalties = collectBonusesPenalties(gameStatusById[gameId]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {game.game_name || '(senza nome)'}
      </Typography>
      <Alert severity="info" sx={{ mb: 1 }}>
        Bonus e penalità si gestiscono in Stato giochi: la classifica viene
        ricalcolata da quei valori. Una modifica manuale qui disattiva
        l&apos;aggiornamento automatico per questo gioco (riattivabile con
        l&apos;interruttore sotto).
      </Alert>
      <FormControlLabel
        sx={{ mb: 2 }}
        control={(
          <Switch
            checked={!game.manual}
            onChange={(e) => setGame({ ...game, manual: !e.target.checked })}
          />
        )}
        label="Aggiornamento automatico dal ricalcolo"
      />

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Bonus e penalità (da Stato giochi)
        </Typography>
        {bonusesPenalties.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nessun bonus o penalità applicati.
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {bonusesPenalties.map((row, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  size="small"
                  color={row.kind === 'bonus' ? 'success' : 'error'}
                  label={`${row.kind === 'bonus' ? '+' : '−'}${row.points} ${row.village}`}
                />
                <Typography variant="body2" color="text.secondary">
                  {[row.scope, row.description].filter(Boolean).join(' · ') ||
                    (row.kind === 'bonus' ? 'bonus' : 'penalità')}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      <JsonForm
        value={game}
        onChange={(nv) => setGame({ ...nv, manual: true })}
        hint={singleGameLeaderboardHint}
        villages={villages}
      />
    </Box>
  );
};

export default LeaderboardGameView;
